/**
 * Photographie des donnees en memoire, et index derives.
 *
 * Toute la couche `analysis/` travaille sur cet instantane plutot que sur la
 * base : c'est ce qui la garde pure, synchrone et testable sans IndexedDB. Pour
 * un budget personnel — quelques milliers d'operations sur dix ans — tout tient
 * largement en memoire, et recalculer a chaque changement coute moins cher
 * qu'entretenir des agregats stockes qui finiraient par diverger.
 */

import type {
  Budget,
  Categorie,
  Cents,
  Compte,
  Exception,
  Id,
  ModeleRepartition,
  Operation,
  Part,
  Recurrence,
  Reglement,
  Tiers,
} from '../domain/types'
import { calculerRepartition, etatCreance, type Creance, type Repartition } from './repartition'

export interface Donnees {
  comptes: Compte[]
  categories: Categorie[]
  tiers: Tiers[]
  operations: Operation[]
  recurrences: Recurrence[]
  exceptions: Exception[]
  modeles: ModeleRepartition[]
  parts: Part[]
  reglements: Reglement[]
  budgets: Budget[]
  moiId: Id
}

export const DONNEES_VIDES: Donnees = {
  comptes: [],
  categories: [],
  tiers: [],
  operations: [],
  recurrences: [],
  exceptions: [],
  modeles: [],
  parts: [],
  reglements: [],
  budgets: [],
  moiId: '',
}

export interface Index {
  d: Donnees
  partsParOperation: Map<Id, Part[]>
  reglementsParPart: Map<Id, Reglement[]>
  operationParId: Map<Id, Operation>
  categorieParId: Map<Id, Categorie>
  tiersParId: Map<Id, Tiers>
  compteParId: Map<Id, Compte>
  modeleParId: Map<Id, ModeleRepartition>
  enfants: Map<Id, Id[]>
  /** Memoisation des repartitions : le bilan annuel et le justificatif
   *  redemandent la meme operation plusieurs fois. */
  repartitions: Map<Id, Repartition<Part>>
}

function grouper<T>(items: readonly T[], cle: (t: T) => Id): Map<Id, T[]> {
  const m = new Map<Id, T[]>()
  for (const it of items) {
    const k = cle(it)
    const l = m.get(k)
    if (l) l.push(it)
    else m.set(k, [it])
  }
  return m
}

export function indexer(d: Donnees): Index {
  const enfants = new Map<Id, Id[]>()
  for (const c of d.categories) {
    if (c.parentId) enfants.set(c.parentId, [...(enfants.get(c.parentId) ?? []), c.id])
  }
  return {
    d,
    partsParOperation: grouper(d.parts, (p) => p.operationId),
    reglementsParPart: grouper(d.reglements, (r) => r.partId),
    operationParId: new Map(d.operations.map((o) => [o.id, o])),
    categorieParId: new Map(d.categories.map((c) => [c.id, c])),
    tiersParId: new Map(d.tiers.map((t) => [t.id, t])),
    compteParId: new Map(d.comptes.map((c) => [c.id, c])),
    modeleParId: new Map(d.modeles.map((m) => [m.id, m])),
    enfants,
    repartitions: new Map(),
  }
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

/** La categorie et toutes ses sous-categories. */
export function perimetreCategorie(ix: Index, categorieId: Id): Set<Id> {
  const out = new Set<Id>([categorieId])
  const pile = [categorieId]
  while (pile.length) {
    for (const e of ix.enfants.get(pile.pop()!) ?? []) {
      if (!out.has(e)) {
        out.add(e)
        pile.push(e)
      }
    }
  }
  return out
}

/** Categorie de premier niveau, pour agreger les statistiques. */
export function categorieRacine(ix: Index, categorieId: Id): Id {
  let c = ix.categorieParId.get(categorieId)
  const vus = new Set<Id>()
  while (c?.parentId && !vus.has(c.id)) {
    vus.add(c.id)
    const parent = ix.categorieParId.get(c.parentId)
    if (!parent) break
    c = parent
  }
  return c?.id ?? categorieId
}

export function nomCategorie(ix: Index, categorieId: Id): string {
  const c = ix.categorieParId.get(categorieId)
  if (!c) return 'Sans catégorie'
  const parent = c.parentId ? ix.categorieParId.get(c.parentId) : undefined
  return parent ? `${parent.nom} › ${c.nom}` : c.nom
}

// ---------------------------------------------------------------------------
// Repartition et charge reelle
// ---------------------------------------------------------------------------

export function repartitionDe(ix: Index, op: Operation): Repartition<Part> {
  const deja = ix.repartitions.get(op.id)
  if (deja) return deja
  // Un revenu ne se repartit pas : il entre entier sur le compte.
  const parts = op.sens === 'depense' ? (ix.partsParOperation.get(op.id) ?? []) : []
  const r = calculerRepartition(op.montant, parts, ix.d.moiId)
  ix.repartitions.set(op.id, r)
  return r
}

/**
 * Ce que l'operation represente **pour moi** : ma quote-part pour une depense,
 * le montant entier pour un revenu.
 *
 * C'est la mesure des budgets et des statistiques. La tresorerie, elle,
 * utilise `op.montant` : voir la decision 4 du plan.
 */
export function chargeReelle(ix: Index, op: Operation): Cents {
  return op.sens === 'depense' ? repartitionDe(ix, op).partMoi : op.montant
}

/** Quote-part a ma charge d'un montant reparti selon un modele. Sert aux
 *  echeances prevues d'une recurrence partagee, qui n'ont pas encore de parts. */
export function chargeSelonModele(ix: Index, montant: Cents, modeleId: Id | null): Cents {
  if (!modeleId) return montant
  const modele = ix.modeleParId.get(modeleId)
  if (!modele) return montant
  return calculerRepartition(montant, modele.parts, ix.d.moiId).partMoi
}

/** Toutes les creances (parts dues par un autre que moi), avec leur etat. */
export function creances(ix: Index): Creance[] {
  const out: Creance[] = []
  for (const op of ix.d.operations) {
    if (op.sens !== 'depense') continue
    const r = repartitionDe(ix, op)
    for (const l of r.lignes) {
      if (l.part.tiersId === ix.d.moiId) continue
      out.push(etatCreance(l.part, l.montant, ix.reglementsParPart.get(l.part.id ?? '') ?? []))
    }
  }
  return out
}
